import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, QrCode, Download } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import EmptyState from "../common/EmptyState";
import {
  getWelcomeQR,
  getSubPromoterQR,
  generateSubPromoterQR,
  emailEventQRCode,
  getTicketQR,
  setTicketQRThreshold,
} from "../../services/eventDetailService";
import { getELQRById } from "../../services/createEventService";
import { color, radius } from "../../styles/theme";

/**
 * QR generation suite, ported from mobile's qr-list-bottom-sheet +
 * qr-list + hooks/use-event-qr-actions.js.
 *
 * gQR (Guest QR) is intentionally omitted: its mobile visibility condition
 * requires either a chat-room "host" flag or `isUser` (plain end-user
 * account) — a Promoter account never satisfies that gate for their own
 * event, so it would never appear for the audience this panel serves.
 *
 * iQR (Personalized QR / dynamic eQR) is intentionally omitted too: it must
 * only ever be generated/displayed inside the app on an authorized,
 * location-verified online device — never in the Promoter Panel or PSA
 * Studio, static or dynamic. eQR (Event QR) below follows the same rule: it
 * is never generated/displayed here either, only requested as an emailed
 * static print version.
 */
const QRModal = ({ event, permissions, onClose }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const { isAdmin, canManageAccess, isFamilyEvent, isARoomEvent, isEventPassed, isEventStarted, isIndividualQR } = permissions;
  const [activeId, setActiveId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [results, setResults] = useState({}); // id -> {image, link, message}
  // Ticket QR's threshold starts unset (TicketThreshold: null) — the first
  // time a promoter opens it, they must pick how many tickets they plan to
  // sell before the QR/link exist at all. showThresholdPrompt swaps the tile
  // list for that one-time inline form instead of a result screen.
  const [showThresholdPrompt, setShowThresholdPrompt] = useState(false);
  const [thresholdInput, setThresholdInput] = useState("");
  const [settingThreshold, setSettingThreshold] = useState(false);

  const showResult = (id, image, link, message) => {
    setResults((prev) => ({ ...prev, [id]: { image, link, message } }));
    setActiveId(id);
  };

  const runTile = async (id, fn) => {
    setLoadingId(id);
    try {
      await fn();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("qrModal.generateError"));
    } finally {
      setLoadingId(null);
    }
  };

  const submitThreshold = async () => {
    const value = Number(thresholdInput);
    if (!Number.isInteger(value) || value < 1) {
      toast.error(t("qrModal.thresholdPrompt.invalid"));
      return;
    }
    setSettingThreshold(true);
    try {
      await setTicketQRThreshold(event.Id, value);
      const res = await getTicketQR(event.Id);
      const result = res.data?.Result ?? res.data;
      if (!result?.QRBytes) throw new Error(res.data?.Message || "Failed to generate QR.");
      setShowThresholdPrompt(false);
      showResult("tQR", `data:image/png;base64,${result.QRBytes}`, result.DynamicLink);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("qrModal.generateError"));
    } finally {
      setSettingThreshold(false);
    }
  };

  const tiles = [
    {
      id: "eQR",
      title: t("qrModal.tiles.eqr.title"),
      subtitle: t("qrModal.tiles.eqr.subtitle"),
      visible: canManageAccess && !isFamilyEvent && !isARoomEvent && !isEventPassed,
      onPress: () =>
        runTile("eQR", async () => {
          // Never generated/displayed here — only requested as an emailed static
          // print version (PNG+PDF attachments sent to the caller's own account email).
          const res = await emailEventQRCode(event.Id);
          const message = res.data?.Message || t("qrModal.eqrEmailSent");
          toast.success(message);
          showResult("eQR", null, null, message);
        }),
    },
    {
      id: "tQR",
      title: t("qrModal.tiles.tqr.title"),
      subtitle: t("qrModal.tiles.tqr.subtitle"),
      // Generated at event creation right alongside eQR, so it shares eQR's
      // visibility gate.
      visible: canManageAccess && !isFamilyEvent && !isARoomEvent && !isEventPassed,
      onPress: () =>
        runTile("tQR", async () => {
          const res = await getTicketQR(event.Id);
          const result = res.data?.Result ?? res.data;
          if (result?.TicketThreshold == null) {
            setThresholdInput("");
            setShowThresholdPrompt(true);
            return;
          }
          if (!result?.QRBytes) throw new Error(res.data?.Message || "Failed to generate QR.");
          showResult("tQR", `data:image/png;base64,${result.QRBytes}`, result.DynamicLink);
        }),
    },
    {
      id: "lQR",
      title: t("qrModal.tiles.lqr.title"),
      subtitle: t("qrModal.tiles.lqr.subtitle"),
      visible: canManageAccess && !isFamilyEvent && !isARoomEvent && !isEventPassed && Boolean(event?.LQRId),
      onPress: () =>
        runTile("lQR", async () => {
          const res = await getELQRById(event.LQRId);
          const data = res.data?.Result ?? res.data;
          if (!data?.QRCodeLink) throw new Error("No location QR available.");
          showResult("lQR", data.QRCodeImage ? `data:image/png;base64,${data.QRCodeImage}` : data.QRCodeLink, data.QRCodeLink);
        }),
    },
    {
      id: "sQR",
      title: t("qrModal.tiles.sqr.title"),
      subtitle: t("qrModal.tiles.sqr.subtitle"),
      visible: isAdmin && !isFamilyEvent && !isARoomEvent,
      onPress: () =>
        runTile("sQR", async () => {
          let res;
          try {
            res = await getSubPromoterQR(event.Id);
            if (res.data?.Status !== "Success") throw new Error("retry");
          } catch {
            res = await generateSubPromoterQR(event.Id);
          }
          const result = res.data?.Result ?? res.data;
          if (!result?.Bytes) throw new Error(res.data?.Message || "Failed to generate QR.");
          showResult("sQR", `data:image/png;base64,${result.Bytes}`, result.ChatRoomLink);
        }),
    },
    {
      id: "pQR",
      title: t("qrModal.tiles.pqr.title"),
      subtitle: t("qrModal.tiles.pqr.subtitle"),
      visible: isIndividualQR
        ? canManageAccess && !isEventStarted && !isFamilyEvent
        : canManageAccess && !isEventPassed && !isFamilyEvent,
      onPress: () =>
        runTile("pQR", async () => {
          const res = await getWelcomeQR(event.Id);
          const result = res.data?.Result ?? res.data;
          if (res.data?.Status !== "Success" || !result?.Bytes) throw new Error(res.data?.Message || "Failed to generate QR.");
          showResult("pQR", `data:image/png;base64,${result.Bytes}`, result.ChatRoomLink);
        }),
    },
  ].filter((t) => t.visible);

  const active = activeId ? results[activeId] : null;
  const activeTile = tiles.find((tile) => tile.id === activeId);

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("qrModal.linkCopied"));
    } catch {
      toast.error(t("qrModal.copyError"));
    }
  };

  const shareLink = async (link, title) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url: link });
        return;
      } catch {
        // user cancelled or share failed — fall through to copy
      }
    }
    copyLink(link);
  };

  const downloadImage = (dataUri, title) => {
    const a = document.createElement("a");
    a.href = dataUri;
    a.download = `${(title || "qr-code").replace(/\s+/g, "-").toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Modal
      title={showThresholdPrompt ? t("qrModal.tiles.tqr.title") : active ? activeTile?.title : t("qrModal.title")}
      onClose={onClose}
      maxWidth={420}
    >
      {showThresholdPrompt ? (
        <div style={s.resultWrap}>
          <button type="button" className="btn btn-ghost btn-sm" style={s.backLink} onClick={() => setShowThresholdPrompt(false)} disabled={settingThreshold}>
            <ChevronLeft size={14} strokeWidth={2.5} /> {t("qrModal.allQrCodes")}
          </button>
          <p style={s.messageText}>{t("qrModal.thresholdPrompt.description")}</p>
          <input
            type="number"
            min="1"
            step="1"
            className="input"
            style={s.linkInput}
            placeholder={t("qrModal.thresholdPrompt.placeholder")}
            value={thresholdInput}
            onChange={(e) => setThresholdInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitThreshold()}
            autoFocus
          />
          <div style={s.resultActions}>
            <Button onClick={submitThreshold} disabled={settingThreshold}>
              {settingThreshold ? t("common:actions.pleaseWait") : t("common:actions.continue")}
            </Button>
          </div>
        </div>
      ) : active ? (
        <div style={s.resultWrap}>
          <button type="button" className="btn btn-ghost btn-sm" style={s.backLink} onClick={() => setActiveId(null)}>
            <ChevronLeft size={14} strokeWidth={2.5} /> {t("qrModal.allQrCodes")}
          </button>
          {active.image && (
            <img src={active.image} alt={activeTile?.title} style={s.qrImage} />
          )}
          {active.message ? (
            <p style={s.messageText}>{active.message}</p>
          ) : active.link ? (
            <>
              <input readOnly value={active.link} className="input" style={s.linkInput} onFocus={(e) => e.target.select()} />
              <div style={s.resultActions}>
                <Button variant="secondary" onClick={() => copyLink(active.link)}>{t("qrModal.copyLink")}</Button>
                <Button onClick={() => shareLink(active.link, activeTile?.title)}>{t("common:actions.share")}</Button>
              </div>
            </>
          ) : (
            <div style={s.resultActions}>
              <Button variant="secondary" icon={Download} onClick={() => downloadImage(active.image, activeTile?.title)}>{t("qrModal.downloadQr")}</Button>
            </div>
          )}
        </div>
      ) : tiles.length === 0 ? (
        <EmptyState icon={QrCode} text={t("qrModal.noQrCodes")} />
      ) : (
        <div style={s.list}>
          {tiles.map((tile) => (
            <button
              key={tile.id}
              type="button"
              className="row-link"
              style={s.tile}
              onClick={tile.onPress}
              disabled={loadingId === tile.id}
            >
              <span style={s.tileIcon}><QrCode size={17} strokeWidth={2} /></span>
              <div style={s.tileText}>
                <span style={s.tileTitle}>{tile.title}</span>
                <span style={s.tileSub}>{tile.subtitle}</span>
              </div>
              <ChevronRight size={16} color={color.gray400} style={{ flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column", gap: 6 },
  tile: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 12px", borderRadius: radius.sm, border: `1px solid ${color.border}`,
    background: color.surface,
  },
  tileIcon: {
    width: 34, height: 34, borderRadius: radius.sm, background: color.info50, color: color.info500,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  tileText: { display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0, textAlign: "left" },
  tileTitle: { fontSize: 13.5, fontWeight: 700, color: color.textPrimary },
  tileSub: { fontSize: 11.5, color: color.textMuted },
  resultWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 14 },
  backLink: { alignSelf: "flex-start", color: color.primary600, padding: "4px 6px" },
  qrImage: { width: 220, height: 220, objectFit: "contain", background: color.gray50, borderRadius: radius.md, border: `1px solid ${color.border}` },
  linkInput: { width: "100%", fontSize: 12, color: color.textSecondary, background: color.gray50 },
  messageText: { fontSize: 13, color: color.textSecondary, textAlign: "center" },
  resultActions: { display: "flex", gap: 10 },
};

export default QRModal;
